import {
  Badge,
  Button,
  EmptyState,
  Menu,
  type MenuItem,
  PageHeader,
  Skeleton,
  buttonClass,
  useConfirm,
  useToast,
} from '@/components';
import { useAuth } from '@/features/auth/AuthProvider';
import { PollPreview } from '@/features/polls/PollPreview';
import { RespondForm } from '@/features/polls/RespondForm';
import { useClosePoll, useDeletePoll, usePoll, usePublishPoll } from '@/features/polls/hooks';
import { A, useNavigate, useParams } from '@solidjs/router';
import { Show } from 'solid-js';

export default function PollDetail() {
  const params = useParams<{ pollId: string }>();
  const query = usePoll(() => params.pollId);
  const { session } = useAuth();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const toast = useToast();
  const publish = usePublishPoll(params.pollId);
  const close = useClosePoll(params.pollId);
  const remove = useDeletePoll(params.pollId);

  const actions = (): MenuItem[] => {
    const poll = query.data?.data;
    if (!poll) return [];
    const items: MenuItem[] = [];
    if (poll.status === 'draft' && poll.canManage) {
      items.push(
        { label: 'Edit draft', onSelect: () => navigate(`/polls/${params.pollId}/edit`) },
        {
          label: 'Publish',
          onSelect: async () => {
            if (
              !(await confirm({
                title: 'Publish this poll?',
                body: 'Questions, options, branching, and response settings will be frozen.',
                confirmLabel: 'Publish',
              }))
            )
              return;
            await publish.mutateAsync(undefined as never);
            toast.success('Poll published');
          },
        },
      );
    }
    if (poll.canViewResults)
      items.push({
        label: 'Results',
        onSelect: () => navigate(`/polls/${params.pollId}/results`),
      });
    if (poll.canManage || session()?.permissions.has('polls:delete'))
      items.push({
        label: 'Delete poll',
        danger: true,
        onSelect: async () => {
          if (
            !(await confirm({
              title: `Delete ${poll.title}?`,
              body: 'This permanently deletes every response.',
              confirmLabel: 'Delete',
              danger: true,
            }))
          )
            return;
          await remove.mutateAsync(undefined as never);
          navigate('/polls');
        },
      });
    return items.map((item) => ({
      ...item,
      disabled: publish.isPending || remove.isPending || close.isPending,
    }));
  };

  return (
    <section>
      <PageHeader
        title={query.data?.data.title ?? 'Poll'}
        sub={
          query.data ? (
            <Badge tone={query.data.data.status === 'open' ? 'success' : 'neutral'}>
              {query.data.data.status}
            </Badge>
          ) : undefined
        }
        actions={
          <div class="flex gap-2">
            <A href="/polls">
              <Button variant="secondary">Back</Button>
            </A>
            <Show when={actions().length}>
              <Menu
                trigger={
                  <>
                    Actions <span aria-hidden="true">▾</span>
                  </>
                }
                triggerClass={buttonClass('secondary')}
                items={actions()}
              />
            </Show>
          </div>
        }
      />
      <div class="mx-auto max-w-6xl p-6">
        <Show when={!query.isPending} fallback={<Skeleton class="h-64" />}>
          <Show
            when={query.data?.data}
            fallback={<EmptyState variant="error" title="Poll not found" />}
          >
            {(poll) => {
              const canDeleteAny = () => session()?.permissions.has('polls:delete') ?? false;
              const canClose = () =>
                (session()?.permissions.has('polls:close') ?? false) &&
                (poll().canManage || canDeleteAny());
              return (
                <>
                  <Show when={poll().description && poll().status !== 'draft'}>
                    <p class="mb-5 whitespace-pre-wrap text-neutral-600 dark:text-neutral-300">
                      {poll().description}
                    </p>
                  </Show>
                  <Show when={poll().status === 'draft'} fallback={<RespondForm poll={poll()} />}>
                    <div class="flex flex-col gap-4">
                      <PollPreview poll={poll()} />
                    </div>
                  </Show>

                  <Show when={poll().status === 'open' && canClose()}>
                    <div class="mt-6 flex justify-end">
                      <Button
                        variant="secondary"
                        loading={close.isPending}
                        onClick={async () => {
                          if (
                            !(await confirm({
                              title: `Close ${poll().title}?`,
                              confirmLabel: 'Close',
                            }))
                          )
                            return;
                          await close.mutateAsync(undefined as never);
                          toast.success('Poll closed');
                        }}
                      >
                        Close now
                      </Button>
                    </div>
                  </Show>
                </>
              );
            }}
          </Show>
        </Show>
      </div>
    </section>
  );
}
